/**
 * Floor plan fixture measurements — tiered dimension chains.
 *
 * Each wall stacks up to four parallel dimension lines outward from the wall.
 * Tier identity (which conceptual layer a line belongs to) is independent
 * of its offset distance. Tiers, innermost → outermost when all four are
 * present, are: T2 (per-group inner detail) → T1 (fixture-group total
 * widths) → T0.5 (opening widths) → T0 (wall total).
 *
 * Architectural drafting convention: each present tier falls into the
 * innermost free slot, so labels never float over empty space. The slot
 * stack is [0.5, 0.95, 1.4, 1.85] m. A bare wall puts T0 at 0.5 m; a
 * wall with only an opening puts T0.5 at 0.5 m and T0 at 0.95 m. Without
 * this, a bare wall's length label would float ~6 ft out with nothing
 * in the gap.
 *
 * Fixture groups: the unit that gets one bracket on T1.
 *   - vanity:  lower-cabinet + any children (sink, countertop, faucet, …)
 *   - tub:     bathtub + its children (tub-support-box, tub-surface)
 *   - shower:  singleton
 *   - toilet:  singleton
 * Composite groups are resolved via the children's `parents[]` array
 * (see shared-lib/tub-surround/index.js for the canonical example).
 */

import { getObjectType } from '@shared-lib/fixture-types';
import { FURNISHING_TYPES } from './measurementConstants';
import { apply3DRotationTo2D } from './geometryUtils';
import { metersToFeetInches } from './floorPlanUtils';
import { getParentWallIds } from './parentWallHelpers';
import { projectPointOntoSegment } from './geometryUtils';

// === Tier model ============================================================

// Slot stack, innermost → outermost. Each present tier on a wall gets
// the next free slot from this list; absent tiers don't reserve space.
//   slot 0 — innermost, hugs the wall
//   slot 3 — outermost
// Tier roles when fully populated:
//   T2   — per-group inner detail, segments span only each group's
//          footprint so multiple groups share a slot without overlap.
//   T1   — fixture-group edges only, single chain across the wall.
//   T0.5 — openings (doors / windows) only, single chain across the wall.
//   T0   — wall total, outermost when present alongside everything else.
// Perpendicular offsets (meters) of each measurement tier's dim line
// from the wall. Exported so the canvas auto-fit can reserve enough
// world-space for the outermost tier when computing default zoom.
// Slots fill innermost-first based on which tiers are present
// (see `slotOffsets` in calculateFixtureMeasurements), so a wall with
// only one tier still has its labels at TIER_SLOTS[0].
export const TIER_SLOTS = [0.4, 0.65, 0.9, 1.15];

const ANCHOR_DISTANCE_THRESHOLD = 0.5;
const OPENING_DISTANCE_THRESHOLD = 0.3;
const MIN_MEASUREMENT_DISTANCE = 0.05;
const MIN_GROUP_SPAN = 0.01;

const FIXTURE_GROUP_DEFS = [
  {
    id: 'vanity',
    anchorType: 'lower-cabinet',
    label: 'Vanity',
    composite: true,
  },
  // The bathtub is the parent — tub-support-box and tub-surface live in its
  // parents[] array. See shared-lib/tub-surround/index.js.
  {
    id: 'tub',
    anchorType: 'bathtub',
    label: 'Tub',
    composite: true,
  },
  { id: 'shower', anchorType: 'shower', label: 'Shower', composite: false },
  { id: 'toilet', anchorType: 'toilet', label: 'Toilet', composite: false },
];

// === Geometry helpers ======================================================

// Get the 2D footprint of a fixture as { center, corners }. Synthesized
// fixtures use {center, rotation, dimensions}. The dimensions field uses
// either an {x,y,z} schema (cabinets, countertops, tubs, doors, …) or a
// {width,height,depth} schema (sinks per shared-lib/vanity-assembly).
// We accept both — without the alias sinks silently return null and
// fall out of every T2 inner-detail emission.
const getObjectMeasurementPoints = (obj) => {
  if (
    !obj ||
    !obj.center ||
    !obj.dimensions ||
    obj.center.x === undefined ||
    obj.center.z === undefined
  ) {
    return null;
  }
  const dimX =
    obj.dimensions.x !== undefined ? obj.dimensions.x : obj.dimensions.width;
  const dimZ =
    obj.dimensions.z !== undefined ? obj.dimensions.z : obj.dimensions.depth;
  if (dimX === undefined || dimZ === undefined) return null;

  const centerX = obj.center.x;
  const centerZ = obj.center.z;
  const width = dimX;
  const depth = dimZ;

  const normalizedDir = apply3DRotationTo2D(obj.rotation);
  const objectAngle = Math.atan2(normalizedDir[1], normalizedDir[0]);
  const cos = Math.cos(objectAngle);
  const sin = Math.sin(objectAngle);

  const halfWidth = width / 2;
  const halfDepth = depth / 2;
  const corners = [
    [-halfWidth, -halfDepth],
    [halfWidth, -halfDepth],
    [halfWidth, halfDepth],
    [-halfWidth, halfDepth],
  ];

  const worldCorners = corners.map(([x, z]) => [
    centerX + x * cos - z * sin,
    centerZ + x * sin + z * cos,
  ]);

  return {
    center: [centerX, centerZ],
    corners: worldCorners,
  };
};

// projectPointOntoSegment now lives in geometryUtils.js (canonical home —
// see the import above).

const pointAlongWall = (wallSegment, t) => [
  wallSegment.start[0] + (wallSegment.end[0] - wallSegment.start[0]) * t,
  wallSegment.start[1] + (wallSegment.end[1] - wallSegment.start[1]) * t,
];

// === Type classification ===================================================

// Delegate to the canonical shared-lib helper so RoomPlan-scanned objects
// (which carry only `category: { toilet: {} }` with no `type` field) resolve
// to the right fixture type. The previous local implementation only checked
// `obj.type` + door/window category, returning null for the category-only
// shape — so toilets / bathtubs / etc. never formed fixture groups and lost
// their T1 measurement callouts.
const getNormalizedFixtureType = (obj) => {
  const type = getObjectType(obj);
  return type === 'unknown' ? null : type;
};

const isFurnishing = (obj) => {
  const type = getNormalizedFixtureType(obj);
  return type && FURNISHING_TYPES.has(type);
};

const isDoor = (obj) => getNormalizedFixtureType(obj) === 'door';
const isWindow = (obj) => getNormalizedFixtureType(obj) === 'window';
// Frameless openings / pass-throughs live under `category.opening` (no door
// or window frame, so they're not caught by isDoor/isWindow). They sit flush
// against a wall just like doors/windows and belong on the same T0.5 chain.
const isOpening = (obj) => !!obj?.category?.opening;

// === Fixture group resolution ==============================================

// Build fixture groups from the room's fixture objects. Composite groups
// (vanity, tub) include every transitive descendant of the anchor — sinks
// parent through countertops, faucets through sinks, etc., so a sink
// reaches the lower-cabinet anchor only after walking parents twice.
const buildFixtureGroups = (fixtureObjects) => {
  if (!fixtureObjects || fixtureObjects.length === 0) return [];

  const byId = new Map();
  fixtureObjects.forEach((o) => {
    if (o?.id) byId.set(o.id, o);
  });

  const isDescendantOf = (obj, anchorId, visited) => {
    if (!obj?.parents) return false;
    if (visited.has(obj.id)) return false;
    visited.add(obj.id);
    for (const p of obj.parents) {
      if (!p?.id) continue;
      if (p.id === anchorId) return true;
      const parent = byId.get(p.id);
      if (parent && isDescendantOf(parent, anchorId, visited)) return true;
    }
    return false;
  };

  const groups = [];
  fixtureObjects.forEach((obj) => {
    const type = getNormalizedFixtureType(obj);
    const def = FIXTURE_GROUP_DEFS.find((d) => d.anchorType === type);
    if (!def) return;

    let members = [obj];
    if (def.composite) {
      const descendants = fixtureObjects.filter(
        (o) => o !== obj && isDescendantOf(o, obj.id, new Set())
      );
      members = [obj, ...descendants];
    }

    groups.push({
      id: `${def.id}:${obj.id}`,
      type: def.id,
      label: def.label,
      anchor: obj,
      members,
    });
  });

  return groups;
};

// Compute a fixture group's footprint along a wall as [startT, endT].
// Also reports which member owns each extremum so T1 / T2 measurement
// endpoints can be tagged with the actual edge-owner's id (e.g., the
// tub-support-box owns the tub group's outer edges, not the bathtub).
// Clamps to [0,1]; returns null if the group has no measurable overlap.
const computeGroupFootprintOnWall = (group, wallSegment) => {
  let minT = Infinity;
  let maxT = -Infinity;
  let minMember = null;
  let maxMember = null;

  group.members.forEach((member) => {
    const pts = getObjectMeasurementPoints(member);
    if (!pts || !pts.corners) return;
    pts.corners.forEach((corner) => {
      const proj = projectPointOntoSegment(corner, wallSegment);
      if (!proj) return;
      if (proj.t < minT) {
        minT = proj.t;
        minMember = member;
      }
      if (proj.t > maxT) {
        maxT = proj.t;
        maxMember = member;
      }
    });
  });
  if (minMember === null || maxMember === null) return null;

  const startT = Math.max(0, minT);
  const endT = Math.min(1, maxT);
  if (endT - startT < MIN_GROUP_SPAN) return null;

  return { startT, endT, startMember: minMember, endMember: maxMember };
};

// Geometric fallback for groups whose anchor has no parentWallIds set
// (legacy data, polygon-mode segments, etc.). Picks every wall whose
// distance to the anchor's center is under threshold — naturally returns
// both walls for a corner-installed shower.
const findGroupParentWallsByGeometry = (group, wallSegments) => {
  const anchorPoints = getObjectMeasurementPoints(group.anchor);
  if (!anchorPoints) return [];
  return wallSegments.filter((ws) => {
    const proj = projectPointOntoSegment(anchorPoints.center, ws);
    return proj && proj.distance < ANCHOR_DISTANCE_THRESHOLD;
  });
};

// Geometric fallback for openings: a wall is a parent if all four corners
// of the opening project flush against it.
const findOpeningParentWallsByGeometry = (opening, wallSegments) =>
  wallSegments.filter((ws) => collectOpeningEdges([opening], ws).length > 0);

// Project an object's footprint onto a wall and return [minT, maxT] of its
// corner projections. Used for fixture inner edges (e.g. bathtub inside
// the tub group's support-box span).
const projectObjectFootprintOntoWall = (obj, wallSegment) => {
  const pts = getObjectMeasurementPoints(obj);
  if (!pts || !pts.corners) return null;
  const ts = pts.corners
    .map((c) => projectPointOntoSegment(c, wallSegment))
    .filter(Boolean)
    .map((p) => p.t);
  if (ts.length === 0) return null;
  return {
    startT: Math.max(0, Math.min(...ts)),
    endT: Math.min(1, Math.max(...ts)),
  };
};

// Per-fixture-type T2 inner waypoints. Each function returns an array of
// `{ t, label, ref }` points to thread between the group's outer edges on
// the T2 line. Returning null means the group has no internal detail to
// dimension and gets no T2 line at all.
//
//   tub      bathtub edges live inside the support-box footprint. The
//            anchor is the bathtub itself; project its corners.
//   vanity   sink centers, sorted along the wall. Anchor is the
//            lower-cabinet; sinks are children.
//   shower   no inner detail.
//   toilet   no inner detail.
const T2_INNER_EXTRACTORS = {
  tub: (group, wallSegment) => {
    const proj = projectObjectFootprintOntoWall(group.anchor, wallSegment);
    if (!proj) return null;
    return [
      { t: proj.startT, label: 'Bathtub', ref: group.anchor },
      { t: proj.endT, label: 'Bathtub', ref: group.anchor },
    ];
  },
  vanity: (group, wallSegment) => {
    const sinks = group.members.filter(
      (m) => getNormalizedFixtureType(m) === 'sink'
    );
    if (sinks.length === 0) return null;
    const points = [];
    sinks.forEach((sink) => {
      const pts = getObjectMeasurementPoints(sink);
      if (!pts) return;
      const proj = projectPointOntoSegment(pts.center, wallSegment);
      if (!proj) return;
      points.push({ t: proj.t, label: 'Sink', ref: sink });
    });
    return points.length > 0 ? points : null;
  },
};

// === Opening detection (doors / windows) ===================================

// Find doors/windows whose footprint lies flush against a wall. Returns one
// entry per opening with [startT, endT] along the wall.
const collectOpeningEdges = (openings, wallSegment) => {
  const result = [];
  openings.forEach((obj) => {
    const points = getObjectMeasurementPoints(obj);
    if (!points || !points.corners) return;

    const ts = [];
    let allClose = true;
    points.corners.forEach((corner) => {
      const proj = projectPointOntoSegment(corner, wallSegment);
      if (!proj) return;
      if (proj.distance > OPENING_DISTANCE_THRESHOLD) allClose = false;
      ts.push(proj.t);
    });
    if (!allClose || ts.length < 2) return;

    const startT = Math.max(0, Math.min(...ts));
    const endT = Math.min(1, Math.max(...ts));
    if (endT - startT < MIN_GROUP_SPAN) return;
    if (endT <= 0 || startT >= 1) return;

    result.push({
      object: obj,
      label: isDoor(obj) ? 'Door' : isWindow(obj) ? 'Window' : 'Opening',
      startT,
      endT,
    });
  });
  return result;
};

// === Per-wall offset direction =============================================

// Pick the perpendicular direction that points away from the room center, so
// dimension lines sit on the exterior side of each wall.
const computeOffsetDir = (wallSegment, wallDir, roomCenter) => {
  const perpDir = [-wallDir[1], wallDir[0]];
  const wallMidpoint = [
    (wallSegment.start[0] + wallSegment.end[0]) / 2,
    (wallSegment.start[1] + wallSegment.end[1]) / 2,
  ];
  const testPoint = [
    wallMidpoint[0] + perpDir[0] * 0.1,
    wallMidpoint[1] + perpDir[1] * 0.1,
  ];
  const distToCenter = Math.sqrt(
    (testPoint[0] - roomCenter[0]) ** 2 + (testPoint[1] - roomCenter[1]) ** 2
  );
  const wallDistToCenter = Math.sqrt(
    (wallMidpoint[0] - roomCenter[0]) ** 2 +
      (wallMidpoint[1] - roomCenter[1]) ** 2
  );
  return distToCenter > wallDistToCenter ? perpDir : [-perpDir[0], -perpDir[1]];
};

// === Measurement record builders ===========================================

// Build a single measurement record in the V2 shape consumed by
// drawMeasurementsV2. The caller passes an explicit perpendicular offset
// (in meters from the wall) so per-group fixture lines can stack at
// distinct distances. Extension lines run from wall to dim line.
const buildMeasurement = ({
  startWorld,
  endWorld,
  distance,
  fromLabel,
  toLabel,
  fromObjectId,
  toObjectId,
  wallIndex,
  wallId,
  tier,
  measurementType,
  offsetDir,
  offsetDistance,
  groupId,
  fixtureType,
  relatedObjectIds,
}) => {
  const offsetStart = [
    startWorld[0] + offsetDir[0] * offsetDistance,
    startWorld[1] + offsetDir[1] * offsetDistance,
  ];
  const offsetEnd = [
    endWorld[0] + offsetDir[0] * offsetDistance,
    endWorld[1] + offsetDir[1] * offsetDistance,
  ];

  return {
    type: 'fixture-node',
    subtype: measurementType,
    tier,
    start: startWorld,
    end: endWorld,
    distance,
    label: metersToFeetInches(distance),
    offsetStart,
    offsetEnd,
    fromLabel,
    toLabel,
    fromObjectId: fromObjectId || null,
    toObjectId: toObjectId || null,
    wallIndex,
    wallId: wallId || null,
    groupId,
    fixtureType,
    // Group-sibling object ids — used for SECONDARY highlight only.
    // Direct endpoints stay on fromObjectId / toObjectId for primary.
    relatedObjectIds: relatedObjectIds || [],
    extensionStartFrom: startWorld,
    extensionStartTo: offsetStart,
    extensionEndFrom: endWorld,
    extensionEndTo: offsetEnd,
  };
};

// Member ids of the feature's fixture group (groups only); openings have none.
const featureGroupMemberIds = (feature) => {
  if (!feature || feature.kind !== 'group') return [];
  return feature.ref.members.map((m) => m.id).filter(Boolean);
};

// === Main entry ============================================================

const computeRoomCenter = (room) => {
  if (room?.center) return [room.center.x, room.center.z];
  const polygonPoints =
    room?.floors?.[0]?.polygonCorners || room?.polygon || [];
  if (polygonPoints.length === 0) return [0, 0];
  let cx = 0;
  let cz = 0;
  polygonPoints.forEach((p) => {
    cx += p.x !== undefined ? p.x : p[0];
    cz += p.z !== undefined ? p.z : p[1];
  });
  return [cx / polygonPoints.length, cz / polygonPoints.length];
};

// Per-wall accumulator. A "feature" is anything with an edge pair on the
// wall — fixture groups and openings. T1 is a single chain across the
// wall through every feature's edges. T2 only carries inner detail for
// fixture groups whose type defines a T2_INNER_EXTRACTOR.
const ensureWallContext = (wallSegment, roomCenter, registry) => {
  if (registry.has(wallSegment)) return registry.get(wallSegment);

  const wallVec = [
    wallSegment.end[0] - wallSegment.start[0],
    wallSegment.end[1] - wallSegment.start[1],
  ];
  const wallLength = Math.sqrt(
    wallVec[0] * wallVec[0] + wallVec[1] * wallVec[1]
  );
  if (wallLength === 0) return null;

  const wallDir = [wallVec[0] / wallLength, wallVec[1] / wallLength];
  const offsetDir = computeOffsetDir(wallSegment, wallDir, roomCenter);

  const ctx = {
    wallSegment,
    wallLength,
    offsetDir,
    // Each entry: { kind: 'group' | 'opening', label, startT, endT, ref }
    // `ref` is the group object (for groups) or the opening object (for openings).
    features: [],
  };
  registry.set(wallSegment, ctx);
  return ctx;
};

// Resolve which wall segments an item (opening or group anchor) renders on.
// Authoritative source is parentWallIds; geometric fallback is used for
// legacy data (no IDs), polygon-mode segments (no wallId on segments), or
// when the stored ids don't resolve to any of this room's wall segments.
const resolveParentWallSegments = (anchorObj, wallById, geometricFallback) => {
  const ids = getParentWallIds(anchorObj);
  if (ids.length > 0) {
    const resolved = ids.map((id) => wallById.get(id)).filter(Boolean);
    // Stored ids can fail to resolve against this room's wall segments
    // (structure/room id mismatch, mid-migration opening shapes). Falling
    // through to geometry here keeps the object's measurements visible
    // instead of silently dropping it from every wall.
    if (resolved.length > 0) return resolved;
  }
  return geometricFallback();
};

// Calculate tiered floor-plan measurements for a room. `allItems` is the
// concatenated list of objects + doors + windows the caller passes in;
// classification happens here.
//
// Iteration order is *object-outer*: for each opening/group we look up its
// parent walls (via `parentWallIds`, with a geometric fallback) and add to
// each parent wall's tier timelines. This makes corner-installed objects
// (e.g., showers spanning two walls) render naturally on every parent wall.
export const calculateFixtureMeasurements = (room, allItems, wallSegments) => {
  if (
    !wallSegments ||
    wallSegments.length === 0 ||
    !allItems ||
    allItems.length === 0
  ) {
    return [];
  }

  const doors = [];
  const windows = [];
  const openings = [];
  const fixtures = [];
  allItems.forEach((item) => {
    if (!item) return;
    if (isDoor(item)) doors.push(item);
    else if (isWindow(item)) windows.push(item);
    else if (isOpening(item)) openings.push(item);
    else if (!isFurnishing(item)) fixtures.push(item);
  });

  const fixtureGroups = buildFixtureGroups(fixtures);
  const roomCenter = computeRoomCenter(room);

  const wallById = new Map();
  wallSegments.forEach((ws) => {
    if (ws.wallId) wallById.set(ws.wallId, ws);
  });

  const wallRegistry = new Map();

  // Pre-populate so every wall in the room gets a T0 (wall total) label,
  // even ones with no openings or fixtures mounted on them. Without this,
  // such walls never enter wallRegistry and the per-wall emit loop below
  // never sees them.
  wallSegments.forEach((ws) => ensureWallContext(ws, roomCenter, wallRegistry));

  // ---------- Loop through openings, render on each parent wall ----------
  [...doors, ...windows, ...openings].forEach((opening) => {
    const parentWalls = resolveParentWallSegments(opening, wallById, () =>
      findOpeningParentWallsByGeometry(opening, wallSegments)
    );

    parentWalls.forEach((ws) => {
      // Even with a known parent wall, we still project to find the
      // opening's [startT, endT] span along that wall.
      const op = collectOpeningEdges([opening], ws)[0];
      if (!op) return;

      const ctx = ensureWallContext(ws, roomCenter, wallRegistry);
      if (!ctx) return;
      ctx.features.push({
        kind: 'opening',
        label: op.label,
        startT: op.startT,
        endT: op.endT,
        ref: opening,
      });
    });
  });

  // ---------- Loop through fixture groups, render on each parent wall ----------
  fixtureGroups.forEach((group) => {
    const parentWalls = resolveParentWallSegments(group.anchor, wallById, () =>
      findGroupParentWallsByGeometry(group, wallSegments)
    );

    parentWalls.forEach((ws) => {
      const proj = computeGroupFootprintOnWall(group, ws);
      if (!proj) return;

      const ctx = ensureWallContext(ws, roomCenter, wallRegistry);
      if (!ctx) return;
      ctx.features.push({
        kind: 'group',
        label: group.label,
        startT: proj.startT,
        endT: proj.endT,
        ref: group,
        startMember: proj.startMember,
        endMember: proj.endMember,
      });
    });
  });

  // ---------- Emit per wall ----------
  const allMeasurements = [];

  Array.from(wallRegistry.values())
    .sort((a, b) => (a.wallSegment.index ?? 0) - (b.wallSegment.index ?? 0))
    .forEach((ctx) => {
      const { wallSegment, wallLength, offsetDir, features } = ctx;

      // Drop any feature whose footprint is degenerate.
      const validFeatures = features
        .filter((f) => f.endT - f.startT >= MIN_GROUP_SPAN)
        .sort((a, b) => a.startT - b.startT);

      // Precompute T2 inner detail per group so we can tell which tiers
      // are actually present before assigning slot offsets. Caching here
      // also avoids re-running the extractors during the T2 emit pass.
      const innerByGroup = new Map();
      validFeatures.forEach((f) => {
        if (f.kind !== 'group') return;
        const extractor = T2_INNER_EXTRACTORS[f.ref.type];
        if (!extractor) return;
        const inner = extractor(f.ref, wallSegment);
        if (inner && inner.length > 0) innerByGroup.set(f, inner);
      });

      // Stack present tiers innermost → outermost into the slot list, so
      // tiers without anything beneath them fall closer to the wall instead
      // of leaving an empty gap. T0 is always present.
      const hasOpenings = validFeatures.some((f) => f.kind === 'opening');
      const hasGroups = validFeatures.some((f) => f.kind === 'group');
      const hasT2 = innerByGroup.size > 0;
      const slotOffsets = {};
      let slotIdx = 0;
      if (hasT2) slotOffsets.t2 = TIER_SLOTS[slotIdx++];
      if (hasGroups) slotOffsets.t1 = TIER_SLOTS[slotIdx++];
      if (hasOpenings) slotOffsets.t0_5 = TIER_SLOTS[slotIdx++];
      slotOffsets.t0 = TIER_SLOTS[slotIdx];

      // T0 (wall total) is emitted for every wall, even ones with no
      // fixtures or openings — otherwise walls without features lose their
      // length label in single-room view.
      allMeasurements.push(
        buildMeasurement({
          startWorld: wallSegment.start,
          endWorld: wallSegment.end,
          distance: wallLength,
          fromLabel: 'Wall',
          toLabel: 'Wall',
          fromObjectId: null,
          toObjectId: null,
          wallIndex: wallSegment.index,
          wallId: wallSegment.wallId,
          tier: 0,
          measurementType: 'wall-total',
          offsetDir,
          offsetDistance: slotOffsets.t0,
          fixtureType: 'wall',
        })
      );

      if (validFeatures.length === 0) return;

      // Build a single chain across the wall for a subset of features
      // (groups → T1, openings → T0.5). Wall edges always bracket.
      // For groups, we tag each measurement with the group's anchor id and
      // groupId so selection links across tiers. For openings the ref is
      // the opening object directly.
      const emitFeatureChain = (features, tier, offsetDistance, pairType) => {
        const items = [
          { point: wallSegment.start, t: 0, label: 'Wall' },
          { point: wallSegment.end, t: 1, label: 'Wall' },
        ];
        features.forEach((f) => {
          // Each endpoint references the actual object whose edge sits at
          // that t — for groups that's the member that contributed the
          // extremum (tub-support-box owns the tub group's outer edges,
          // not the bathtub). Openings own both their own edges.
          const startObjectId =
            f.kind === 'group' ? f.startMember.id : f.ref.id;
          const endObjectId = f.kind === 'group' ? f.endMember.id : f.ref.id;
          const groupId = f.kind === 'group' ? f.ref.id : undefined;
          const fixtureType =
            f.kind === 'group'
              ? f.ref.type
              : isDoor(f.ref)
                ? 'door'
                : isWindow(f.ref)
                  ? 'window'
                  : undefined;

          const groupMemberIds = featureGroupMemberIds(f);
          items.push(
            {
              point: pointAlongWall(wallSegment, f.startT),
              t: f.startT,
              label: f.label,
              featureRef: f.ref,
              feature: f,
              refObjectId: startObjectId,
              groupId,
              fixtureType,
              groupMemberIds,
              isFeatureStart: true,
            },
            {
              point: pointAlongWall(wallSegment, f.endT),
              t: f.endT,
              label: f.label,
              featureRef: f.ref,
              feature: f,
              refObjectId: endObjectId,
              groupId,
              fixtureType,
              groupMemberIds,
              isFeatureEnd: true,
            }
          );
        });
        items.sort((a, b) => a.t - b.t);

        for (let i = 0; i < items.length - 1; i++) {
          const current = items[i];
          const next = items[i + 1];
          const distance = Math.hypot(
            next.point[0] - current.point[0],
            next.point[1] - current.point[1]
          );
          if (distance <= MIN_MEASUREMENT_DISTANCE) continue;

          const isPair =
            current.isFeatureStart &&
            next.isFeatureEnd &&
            current.featureRef === next.featureRef;

          // Secondary-highlight set: union of bordering features' group
          // members, minus whatever's already on the primary endpoints.
          const fromId = current.refObjectId || null;
          const toId = next.refObjectId || null;
          const memberPool = new Set([
            ...(current.groupMemberIds || []),
            ...(next.groupMemberIds || []),
          ]);
          if (fromId) memberPool.delete(fromId);
          if (toId) memberPool.delete(toId);
          const relatedObjectIds = Array.from(memberPool);

          allMeasurements.push(
            buildMeasurement({
              startWorld: current.point,
              endWorld: next.point,
              distance,
              fromLabel: current.label,
              toLabel: isPair ? current.label : next.label,
              fromObjectId: fromId,
              toObjectId: toId,
              wallIndex: wallSegment.index,
              wallId: wallSegment.wallId,
              tier,
              measurementType: isPair ? pairType : 'pier',
              offsetDir,
              offsetDistance,
              groupId: current.groupId || next.groupId,
              fixtureType: current.fixtureType || next.fixtureType,
              relatedObjectIds,
            })
          );
        }
      };

      // ---------- T1: fixture-group chain ----------
      const groupFeatures = validFeatures.filter((f) => f.kind === 'group');
      if (groupFeatures.length > 0) {
        emitFeatureChain(groupFeatures, 1, slotOffsets.t1, 'group-width');
      }

      // ---------- T0.5: opening chain ----------
      const openingFeatures = validFeatures.filter((f) => f.kind === 'opening');
      if (openingFeatures.length > 0) {
        emitFeatureChain(
          openingFeatures,
          0.5,
          slotOffsets.t0_5,
          'opening-width'
        );
      }

      // ---------- T2: per-group inner detail ----------
      // Each group's segments span only its own footprint, so multiple
      // groups can share the T2 slot without overlapping each other.
      validFeatures.forEach((f) => {
        if (f.kind !== 'group') return;
        const inner = innerByGroup.get(f);
        if (!inner) return;

        // Bracket the inner waypoints with the group's outer edges. Each
        // endpoint references the *actual* object whose edge sits at that
        // t — for tubs that's the support-box, not the bathtub anchor.
        const innerTimeline = [
          {
            t: f.startT,
            point: pointAlongWall(wallSegment, f.startT),
            label: f.label,
            refObjectId: f.startMember.id,
            isGroupStart: true,
          },
          ...inner
            .slice()
            .sort((a, b) => a.t - b.t)
            .map((p) => ({
              t: p.t,
              point: pointAlongWall(wallSegment, p.t),
              label: p.label,
              refObjectId: p.ref?.id,
            })),
          {
            t: f.endT,
            point: pointAlongWall(wallSegment, f.endT),
            label: f.label,
            refObjectId: f.endMember.id,
            isGroupEnd: true,
          },
        ];

        const allGroupMemberIds = featureGroupMemberIds(f);
        for (let i = 0; i < innerTimeline.length - 1; i++) {
          const current = innerTimeline[i];
          const next = innerTimeline[i + 1];
          const distance = Math.hypot(
            next.point[0] - current.point[0],
            next.point[1] - current.point[1]
          );
          if (distance <= MIN_MEASUREMENT_DISTANCE) continue;

          const fromId = current.refObjectId || null;
          const toId = next.refObjectId || null;
          // Secondary highlight = the rest of the group's members.
          const relatedObjectIds = allGroupMemberIds.filter(
            (id) => id !== fromId && id !== toId
          );

          allMeasurements.push(
            buildMeasurement({
              startWorld: current.point,
              endWorld: next.point,
              distance,
              fromLabel: current.label,
              toLabel: next.label,
              fromObjectId: fromId,
              toObjectId: toId,
              wallIndex: wallSegment.index,
              wallId: wallSegment.wallId,
              tier: 2,
              measurementType: 'group-inner',
              offsetDir,
              offsetDistance: slotOffsets.t2,
              groupId: f.ref.id,
              fixtureType: f.ref.type,
              relatedObjectIds,
            })
          );
        }
      });
    });

  return allMeasurements;
};
